/** @ken/module-invoicing — frontend entry point.
 *
 *  0.2.0 — credit_memos fold-in (Sprint 2 mig 0184/0185). Adds
 *  /credit-memos sub-list page + the Credits tab on the customer +
 *  invoice detail pages (read via /api/modules/invoicing/credit-memos/*).
 */

import {
  lazyScreen,
  type AnyRoute,
  type CreateModuleRoutes,
} from "@ken/cockpit-types";
import { createRoute, redirect } from "@tanstack/react-router";

import type { InvoiceStorySearch } from "./routes/story/invoice-story-detail";

// One loader per screen file, shared by every screen that file exports, so the
// route table carries each file's import (and its preload list) once (SF-BUNDLE).
const loadInvoiceStoryTabs = () => import("./routes/story/invoice-story-tabs");

// Screens download the first time they are opened (ADR-040, BND-01). The
// route table below stays eager: paths, params, validateSearch, redirects.
const CreditMemoListPage = lazyScreen(
  () => import("./routes/credit-memo-list"),
  "CreditMemoListPage",
);
const InvoiceCreatePage = lazyScreen(
  () => import("./routes/invoice-create"),
  "InvoiceCreatePage",
);
const InvoiceEditPage = lazyScreen(
  () => import("./routes/invoice-edit"),
  "InvoiceEditPage",
);
const InvoiceListPage = lazyScreen(
  () => import("./routes/invoice-list"),
  "InvoiceListPage",
);
const InvoiceStoryDetail = lazyScreen(
  () => import("./routes/story/invoice-story-detail"),
  "InvoiceStoryDetail",
);
const InvoiceActivityRoute = lazyScreen(
  loadInvoiceStoryTabs,
  "InvoiceActivityRoute",
);
const InvoiceLineItemsRoute = lazyScreen(
  loadInvoiceStoryTabs,
  "InvoiceLineItemsRoute",
);
const InvoiceNotesRoute = lazyScreen(
  loadInvoiceStoryTabs,
  "InvoiceNotesRoute",
);
const InvoiceOverviewRoute = lazyScreen(
  loadInvoiceStoryTabs,
  "InvoiceOverviewRoute",
);
const InvoicePaymentsRoute = lazyScreen(
  loadInvoiceStoryTabs,
  "InvoicePaymentsRoute",
);
const InvoiceRelatedRoute = lazyScreen(
  loadInvoiceStoryTabs,
  "InvoiceRelatedRoute",
);
// ADR-0251 / ADR-0277 (SS-B4.9): the customer's Invoices tab, Invoicing's own
// record tab (manifest `frontend.record_tabs`).
export const recordTabs = {
  CustomerInvoicesTab: lazyScreen(
    () => import("./components/customer-invoices-tab"),
    "CustomerInvoicesTab",
  ),
};

export {
  RecordPaymentDialog,
  RecordPaymentButton,
  type RecordPaymentInvoiceContext,
} from "./components/record-payment-dialog";

export const createRoutes: CreateModuleRoutes = (parentRoute: AnyRoute) => {
  const listRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "invoices",
    // The list's filter/search/highlight are URL-borne so back-navigation
    // from the story page restores them (ADR-0081 §3). validateSearch keeps
    // them typed + reload-proof; the manual writeQueryParams is retired.
    validateSearch: (raw: Record<string, unknown>): InvoiceListSearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["highlight"] === "string" && raw["highlight"]
        ? { highlight: raw["highlight"] }
        : {}),
      // POP-01 (ADR-0229): the record open in the pop-up, so a reload
      // reopens it and the browser's back button closes it.
      ...(typeof raw["open"] === "string" && raw["open"]
        ? { open: raw["open"] }
        : {}),
      // POP-02 (ADR-0230): the pop-up's open tab.
      ...(typeof raw["tab"] === "string" && raw["tab"]
        ? { tab: raw["tab"] }
        : {}),
    }),
    component: InvoiceListPage,
  });

  const createRouteEntry = createRoute({
    getParentRoute: () => parentRoute,
    path: "invoices/new",
    component: InvoiceCreatePage,
  });

  const creditMemosRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "credit-memos",
    component: CreditMemoListPage,
  });

  // Edit (backlog B2): a focused FULL-PAGE form, deep-linkable, NOT nested
  // under the story shell. Parented directly to the module root so it renders
  // standalone (no StoryPageDetail header/tabs around it). Registered BEFORE
  // the `invoices/$id` parent so the `/edit` literal segment is matched.
  const editRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "invoices/$id/edit",
    component: InvoiceEditPage,
  });

  // ── Story-page detail (ADR-0081): parent invoices/$id shell + routed
  //    tab children + an index redirect to …/overview. ────────────────────
  const detailRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "invoices/$id",
    // The list's filter/search + the originating row id ride into the detail
    // route so Prev/Next walks the SAME filtered set and back restores state.
    validateSearch: (raw: Record<string, unknown>): InvoiceStorySearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["from"] === "string" && raw["from"]
        ? { from: raw["from"] }
        : {}),
    }),
    component: InvoiceStoryDetail,
  });

  // Index redirect invoices/$id → …/overview (the default tab).
  const detailIndexRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "/",
    beforeLoad: ({ params }) => {
      throw redirect({
        to: "/$tenantSlug/invoices/$id/overview",
        params: params as never,
        replace: true,
      });
    },
  });

  const overviewRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "overview",
    component: InvoiceOverviewRoute,
  });
  const lineItemsRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "line-items",
    component: InvoiceLineItemsRoute,
  });
  const paymentsRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "payments",
    component: InvoicePaymentsRoute,
  });
  const relatedRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "related",
    component: InvoiceRelatedRoute,
  });
  const activityRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "activity",
    component: InvoiceActivityRoute,
  });
  const notesRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "notes",
    component: InvoiceNotesRoute,
  });

  (detailRoute as unknown as { addChildren: (c: unknown) => void }).addChildren(
    [
      detailIndexRoute,
      overviewRoute,
      lineItemsRoute,
      paymentsRoute,
      relatedRoute,
      activityRoute,
      notesRoute,
    ],
  );

  return [
    listRoute,
    createRouteEntry,
    editRoute,
    creditMemosRoute,
    detailRoute,
  ];
};

/** List search params (filter/search/highlight) — URL-borne for restore. */
export interface InvoiceListSearch {
  q?: string;
  filter_status?: string;
  highlight?: string;
  /** POP-01: the record open in the pop-up over the list. */
  open?: string;
  /** POP-02: the pop-up's open tab (absent: Overview). */
  tab?: string;
}
